import { usePageMeta } from "@/lib/usePageMeta";
import { useCopy } from "@/lib/copy";
import { PageHeader, SectionHeading } from "@/components/site/Content";
import { IllustrativeBadge } from "@/components/site/Brand";
import { SmartImage } from "@/components/site/Media";
import { Rich } from "@/components/site/Rich";

const range = (n) => Array.from({ length: n }, (_, i) => i + 1);

const Block = ({ label, text, testId }) => (
  <div className="border-t-2 border-brand pt-6" data-testid={testId}>
    <p className="eyebrow text-brand-green-dark">{label}</p>
    <p className="mt-4 whitespace-pre-line text-lg font-semibold leading-relaxed text-ink md:text-xl">{text}</p>
  </div>
);

const Section = ({ id, tone, eyebrow, title, intro, aside, children }) => (
  <section className={tone === "mist" ? "bg-mist" : "bg-white"} aria-labelledby={`${id}-heading`} data-testid={`water-${id}`}>
    <div className="container-x py-12 md:py-16">
      <SectionHeading id={`${id}-heading`} eyebrow={eyebrow} title={title} intro={intro && <Rich text={intro} />} action={aside} />
      {children}
    </div>
  </section>
);

const Overview = ({ t }) => (
  <section className="container-x grid gap-12 py-12 md:py-16 lg:grid-cols-12" data-testid="water-overview">
    <div className="border-l-4 border-brand-green pl-6 lg:col-span-12" data-testid="water-why">
      <p className="eyebrow text-brand-green-dark">{t("water.why.label")}</p>
      <p className="mt-3 text-3xl font-extrabold leading-tight tracking-tight text-brand sm:text-4xl xl:text-5xl" data-testid="water-why-text">{t("water.why.text")}</p>
    </div>
    <div className="space-y-12 lg:col-span-7">
      <Block label={t("water.vision.label")} text={t("water.vision.text")} testId="water-vision" />
      <Block label={t("water.mission.label")} text={t("water.mission.text")} testId="water-mission-statement" />
    </div>
    <SmartImage src={t("water.header.image")} alt={t("water.header.imageAlt")} ratio="aspect-[4/3]" className="self-start lg:col-span-5" />
  </section>
);

const Outcomes = ({ t }) => (
  <ol className="grid gap-px border border-line bg-line md:grid-cols-2 lg:grid-cols-5" data-testid="water-outcomes">
    {range(5).map((i) => (
      <li key={i} className="bg-white p-6" data-testid={`water-outcome-${i}`}>
        <span className="text-sm font-extrabold text-brand-green-dark">O{i}</span>
        <h3 className="mt-3 font-extrabold leading-snug text-brand">{t(`water.outcome${i}.title`)}</h3>
        {t(`water.outcome${i}.text`) && <p className="mt-3 text-sm leading-relaxed text-ink-muted">{t(`water.outcome${i}.text`)}</p>}
      </li>
    ))}
  </ol>
);

const Roles = ({ t }) => (
  <ol className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4" data-testid="water-roles">
    {range(4).map((i) => (
      <li key={i} className="border-l-4 border-brand-green pl-6" data-testid={`water-role-${i}`}>
        <h3 className="text-2xl font-extrabold tracking-tight text-brand">{t(`water.role${i}.title`)}</h3>
        {t(`water.role${i}.text`) && <p className="mt-3 text-sm leading-relaxed text-ink-muted">{t(`water.role${i}.text`)}</p>}
      </li>
    ))}
  </ol>
);

const Programmes = ({ t }) => (
  <ol className="divide-y divide-line border-y border-line" data-testid="water-programmes">
    {range(5).map((i) => (
      <li key={i} className="grid gap-3 py-6 md:grid-cols-12 md:gap-8" data-testid={`water-programme-${i}`}>
        <span className="text-sm font-extrabold text-brand-green-dark md:col-span-1">{String(i).padStart(2, "0")}</span>
        <h3 className="font-extrabold text-brand md:col-span-4">{t(`water.programme${i}.title`)}</h3>
        <p className="text-sm leading-relaxed text-ink-muted md:col-span-7">{t(`water.programme${i}.text`)}</p>
      </li>
    ))}
  </ol>
);

const SoFar = ({ t }) => (
  <ol className="relative max-w-3xl border-l-2 border-brand/20 pl-8 md:pl-10" data-testid="water-initiatives">
    {range(6).filter((i) => t(`water.initiative${i}.title`)).map((i) => (
      <li key={i} className="relative pb-10 last:pb-0" data-testid={`water-initiative-${i}`}>
        <span className="absolute -left-[41px] top-1 h-4 w-4 rounded-full border-4 border-mist bg-brand-green ring-2 ring-brand md:-left-[49px]" aria-hidden="true" />
        {t(`water.initiative${i}.date`) && <p className="eyebrow text-brand-green-dark">{t(`water.initiative${i}.date`)}</p>}
        <h3 className="mt-2 text-xl font-extrabold text-brand">{t(`water.initiative${i}.title`)}</h3>
        <p className="mt-2 leading-relaxed text-ink-muted"><Rich text={t(`water.initiative${i}.text`)} /></p>
      </li>
    ))}
  </ol>
);

export default function WaterMission() {
  const t = useCopy();
  usePageMeta("Water Mission: Water Research & Innovation in Bengaluru", "BRaIN's Water Mission brings Bengaluru research and innovation together for a water-secure and water-positive Bengaluru.");
  return (
    <div data-testid="water-mission-page">
      <PageHeader eyebrow={t("water.header.eyebrow")} title={t("water.header.title")} intro={<Rich text={t("water.header.intro")} />} />
      <Overview t={t} />
      <Section id="opportunity" tone="mist" eyebrow={t("water.opportunity.eyebrow")} title={t("water.opportunity.title")}>
        <p className="max-w-3xl text-lg leading-relaxed text-ink"><Rich text={t("water.opportunity.text")} /></p>
      </Section>
      <Section id="change" eyebrow={t("water.change.eyebrow")} title={t("water.change.title")} intro={t("water.change.intro")}><Outcomes t={t} /></Section>
      <Section id="role" tone="mist" eyebrow={t("water.role.eyebrow")} title={t("water.role.title")}><Roles t={t} /></Section>
      <Section id="programme-areas" eyebrow={t("water.programmes.eyebrow")} title={t("water.programmes.title")} intro={t("water.programmes.intro")}
        aside={t("water.programmes.badge") && <IllustrativeBadge label={t("water.programmes.badge")} />}>
        <Programmes t={t} />
      </Section>
      <Section id="so-far" tone="mist" eyebrow={t("water.initiatives.eyebrow")} title={t("water.initiatives.title")}><SoFar t={t} /></Section>
    </div>
  );
}
